<template>
  <article class="product-item relative flex flex-col flex-grow-1 overflow-hidden">
    <ProductItemToast
      class="pt-3 px-3"
      :toast-operation="toastObject"
    />
    <div class="px-4 sm:px-3 md:px-4 pt-4 sm:pt-3 md:pt-4">
      <div class="catalog__grid-img mb-4">
        <RemoteResponsiveImage
          :placeholder="true"
          placeholder-height="75"
          :product-image-data="product.images[0]?.data"
          img-class="w-100 h-100"
          :lazy-load="lazyLoad"
          width="600"
          height="450"
          :alt="product.images[0].alt || product.name"
          sizes="(min-width: 1200px) 25vw, (min-width: 992px) 20vw, (min-width: 600px) 35vw, (min-width: 500px) 50vw, 70vw"
        />
      </div>
    </div>
    <DiscountBubble
      v-if="product.hasDiscount || (parseFloat(product.plusClubPrice) > 0 && getCurrentCustomer?.inPlusClub)"
      :price-with-discount="parseFloat((parseFloat(product.plusClubPrice) > 0) && getCurrentCustomer?.inPlusClub ? product.plusClubPrice : product.price)"
      :price-without-discount="parseFloat(product.priceWithoutDiscount)"
      :currency="product.currency"
    />
    <div
      v-if="(product.name.includes('Premium') || product.name.includes('Prémium')) && !product.name.includes('Birch')"
      class="stamp-holder mx-4 sm:mx-3 md:mx-4"
    >
      <div

        class="special-stamp stamp-grid pr-3 py-1 rounded-pill"
      >
        <div class="stamp-box">
          <StampIconSvg
            class="icon stamp-icon"
            width="62"
            height="62"
          />
          <div class="flag-mask">
            <StampItalyFlagSvg
              class="icon"
              width="25"
              height="23"
            />
          </div>
        </div>
        <span class="m-0 font-bold inline-block">{{ t('own_piece_of_italy') }}</span>
      </div>
    </div>
    <div class="px-4 sm:px-3 md:px-4 flex flex-col flex-fill">
      <div class="flex mb-2 items-center">
        <div
          v-if="!product.starRating || (product.starRating && parseInt(product.starRating) === 0)"
          class="mb-2 md:flex hidden"
          style="height: 24px;"
        />
        <ProductStarsRating
          v-else
          :rating="product.starRating"
          :rating-sum="product.starRatingCommentsSum"
          :show-origin="true"
          add-class="xxl:justify-start"
          theme="light"
        />
        <template v-if="product.commentsSum">
          <hr
            v-if="product.starRating && parseInt(product.starRating) > 0"
            class="rating-divider bg-primary mx-2 my-0"
          >
          <span>
            <i class="mi-comment" /> {{ product.commentsSum }}
          </span>
        </template>
      </div>
      <h2 class="mb-2 text-lg font-medium font-family-base">
        <router-link
          :to="productPath"
          class="text-primary no-decoration stretched-link zindex-1"
        >
          {{ product.name }}
        </router-link>
      </h2>
      <!-- short product description (with no html tags, and 170 length max) -->
      <div
        v-if="product.positives"
        class="product-item__description collapse text-sm text-muted show mb-n1"
        v-html="product.positives"
      />
      <div
        v-else
        class="product-item__description collapse text-sm text-muted show mb-n1"
      >
        <span
          v-if="product.state === 'weather_too_hot'"
          class="text-info"
        >{{ t('selected_chocolate_products') }}&nbsp;</span>
        <span v-html="product.shortDescription || shortDescription" />
      </div>
      <div class="xl:mt-4 mt-3 flex flex-row items-center">
        <div
          v-for="property in marketingProperties"
          :key="property.id"
          v-tooltip="{ content: property.name, theme: 'baseTooltip' }"
          class="col-auto pl-0 pr-0 mr-2 mt-1 relative zindex-2"
        >
          <i :class="`mi-${propertyIcon(property.systemName)} icon-property mi-no-mx mi-lh-1`" />
        </div>
        <div
          v-if="product.isIngredient"
          class="col-auto mt-1 ml-auto hidden md:block"
        >
          <button
            id="btn-close"
            type="button"
            class="btn btn-outline-warning p-1 rounded-circle relative zindex-2"
            data-toggle="product-detail-modal"
            aria-label="product info"
            @click="infoBtnClicked"
          >
            <i class="mi-info" />
          </button>
        </div>
      </div>
      <footer
        class="form-row mt-auto"
        :class="product.plusClubPrice && parseInt(product.plusClubPrice) !== 0 ? 'mb-1' : 'mb-5'"
      >
        <div
          class="col-6 flex flex-col justify-center pt-2"
        >
          <p class="mb-0 flex text-lg">
            <strong class="text-nowrap mr-2 sm:mr-1 md:mr-2 xl:mr-3 xxl:mr-4">
              <span
                v-if="product.quantityDiscounts"
                :class="{
                  'line-through text-danger': parseFloat(product.plusClubPrice) > 0 && getCurrentCustomer?.inPlusClub,
                }"
              >
                <ProductPrice
                  :price-first="product.quantityDiscounts[0].price"
                  :price-second="product.quantityDiscounts.at(-1)?.price"
                  :plus-club-price="product.plusClubPrice"
                  :in-plus-club="getCurrentCustomer?.inPlusClub"
                  :currency="product.currency"
                  :show-from-text="t('from')"
                />
              </span>
              <span v-else>
                <ProductPrice
                  :price-first="product.price"
                  :currency="product.currency"
                />
              </span>
              <span
                v-if="(product.weight > 0 || product.volume > 0) && (!product.quantityDiscounts || !product.hasDiscount)"
                class="small text-nowrap text-muted"
              > /{{ product.unit === 'g' ? product.weight : product.volume }} {{ product.unit }}</span>
            </strong>
          </p>
          <p
            v-if="product.hasDiscount && (parseFloat(product.plusClubPrice) > 0 || !getCurrentCustomer?.inPlusClub)"
            class="mb-0 flex text-lg"
          >
            <strong class="line-through text-danger text-sm text-nowrap mr-2 sm:mr-1 md:mr-2 xl:mr-3 xxl:mr-4">
              <ProductPrice
                :price-first="product.priceWithoutDiscount"
                :currency="product.currency"
              />
            </strong>
          </p>
          <p
            v-if="product.pricePerPortion && product.pricePerPortion !== 'Infinity'"
            class="small mb-0 text-muted"
          >
            ({{ parseFloat(product.pricePerPortion).toFixed(2) }} {{ product.currency }} / 100 {{ product.unit }})
          </p>
        </div>
        <div
          v-if="status !== 'cementery'"
          class="col-6 flex flex-col px-0 justify-center pt-2 zindex-2"
          :class="{ 'mt-auto': product.hasDiscount }"
        >
          <AddToCartButton
            v-if="!product.isIngredient && product.state === 'available'"
            v-close-popper
            :product="product"
            :show-decrement-button="true"
            :gtm-source="isSearchPage ? 'search-results' : undefined"
            @cart-operation="(operation) => { toastObject = operation; }"
          />
          <PushToMixButton
            v-else-if="product.isIngredient && product.state === 'available'"
            :product="product"
          />
          <ButtonState
            v-else
            :product="product"
            :state="product.state"
          />
        </div>
        <div
          v-if="status === 'cementery'"
          class="col-12 sm:col-6 flex flex-col px-0 pt-2"
        >
          <ButtonState
            :product="product"
            added-class="zindex-1 relative mb-2"
            state="revive_me"
            :stretched="false"
          />
          <ButtonState
            :product="product"
            added-class="zindex-1 relative"
            state="mix_me"
            :stretched="false"
          />
        </div>
      </footer>
    </div>
    <div
      v-if="product.plusClubPrice && parseFloat(product.plusClubPrice) > 0"
      class="flex items-center pt-2 px-4 sm:px-3 md:px-4 flex-wrap bg-primary-450"
    >
      <p class="mb-0 flex text-lg mb-1">
        <strong class="text-nowrap mr-1">
          <ProductPrice
            :price-first="product.plusClubPrice"
            :currency="product.currency"
          />
        </strong>
      </p>
      <router-link
        :to="{ name: 'productPath', params: { permalink: t('voucher_permalink') } }"
        class="flex justify-start items-center mb-1 no-decoration relative zindex-2"
      >
        <MixitPlusLogoSvg
          width="106"
          class="mr-1 relative mt-1"
          plus-color="#ffffff"
        />
        <span class="text-nowrap">{{ t('mixit_plus_price') }}</span>
        <span class="flex border border-primary border-2 text-primary p-0 rounded-circle ml-2">
          <i class="mi-question icon-12" />
        </span>
      </router-link>
    </div>
    <!-- video play -->
    <ButtonPlayVideo
      v-if="product.videoUrl"
      class="absolute left-0 top-0 m-2 md:m-3 lg:m-4"
      :video-url="product.videoUrl"
    />
    <!-- add to favourite -->
    <Favorite
      v-if="!product.isIngredient"
      :id="product.id"
      btn-class="icon bg-white"
      class="absolute top-0 right-0 fav-right zindex-2"
    />
  </article>
</template>

<script setup>
import { storeToRefs } from 'pinia'
import { striptags } from 'striptags'
import { computed, defineAsyncComponent, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

import ApiServiceV1 from '@/api/adminApi/AdminApiV1'
import { propertyIcon } from '@/components/svg/properties/propertiesSvgs'
import { numberToCurrency } from '@/mixins/numberToCurrency'
import { useAppStore } from '@/stores/AppStore'
import { useModalStore } from '@/stores/ModalStore'

const AddToCartButton = defineAsyncComponent(() => import('@/components/buttons/ButtonAddToCartButton.vue'))
const PushToMixButton = defineAsyncComponent(() => import('@/components/buttons/ButtonPushToMixButton.vue'))
const ButtonState = defineAsyncComponent(() => import('@/components/buttons/ButtonState.vue'))
const ButtonPlayVideo = defineAsyncComponent(() => import('@/components/buttons/ButtonPlayVideo.vue'))
const Favorite = defineAsyncComponent(() => import('@/components/buttons/ButtonFavorite.vue'))
const DiscountBubble = defineAsyncComponent(() => import('@/components/general/BaseDiscountBubble.vue'))
const RemoteResponsiveImage = defineAsyncComponent(() => import('@/components/general/BaseRemoteResponsiveImage.vue'))
const StampIconSvg = defineAsyncComponent(() => import('@/components/svg/stamp/StampIconSvg.vue'))
const StampItalyFlagSvg = defineAsyncComponent(() => import('@/components/svg/stamp/StampItalyFlagSvg.vue'))
const MixitPlusLogoSvg = defineAsyncComponent(() => import('@/components/svg/MixitPlusLogoSvg.vue'))
const ProductStarsRating = defineAsyncComponent(() => import('@/components/products/ProductStarsRating.vue'))
const ProductItemToast = defineAsyncComponent(() => import('@/components/products/ProductItemToast.vue'))
const ProductPrice = defineAsyncComponent(() => import('@/components/products/ProductPrice.vue'))
const appStore = useAppStore()
const { getCurrentCustomer } = storeToRefs(appStore)

const props = defineProps({
  product: {
    type: Object,
    required: true,
  },

  infoClickBehaviour: {
    type: String,
    default: 'modal',
  },

  status: {
    type: String,
    default: 'default'
  },

  lazyLoad: {
    type: Boolean,
    required: false,
  },

  isSearchPage: {
    type: Boolean,
    default: false,
  }
})

const toastObject = ref();

const router = useRouter()
const { openModal } = useModalStore()
const { t } = useI18n()

const shortDescription = ref('')
const productPath = ref({
  name: 'productPath',
  params: { permalink: props.product.permalink },
})

const marketingProperties = computed(() => {
  if (!props.product) return []
  return props.product.properties.filter((p) => p.propertyType === 'marketing')
})

const showProductDetailModal = async (permalink) => {
  const {
    data: { product },
  } = await ApiServiceV1.get(`products/${permalink}`)

  openModal({
    id: 'product-detail-modal',
    title: product.name,
    params: {
      product,
    },
  })
}

const createShortDescription = () => {
  shortDescription.value = props.product.description ? striptags(props.product.description).slice(0, 140) : ''
}

const infoBtnClicked = async () => {
  await (props.infoClickBehaviour === 'modal'
    ? showProductDetailModal(props.product.permalink)
    : router.push({ name: 'productPath', params: { permalink: props.product.permalink } }))
}

onMounted(() => {
  createShortDescription()
})
</script>

<style lang="scss">
.icon-property{ font-size: 30px; }
.icon-question {
  font-size: 12px;
  margin: 1px
}
.rating-divider {
  width: 1px;
  height: 20px;
}
.rating-divider {
  width: 1px;
  height: 20px;
}
</style>

<i18n>
  {
    "cs": {
    "composition": "Složení",
    "from": "Od",
    "mixit_plus_price": "cena",
    "own_piece_of_italy": "Zažijte vlastní kousek Itálie",
    "play_video": "Shlédnout video",
    "selected_chocolate_products": "Jsem u ledu! Z důvodu vysokých teplot mě poslali do chládku.",
    "voucher_permalink": "mixit-plus-na-12-mesicu"
},
    "sk": {
    "composition": "Zloženie",
    "from": "Od",
    "mixit_plus_price": "cena",
    "own_piece_of_italy": "Zažite vlastný kúsok Talianska",
    "play_video": "Pozrieť video",
    "selected_chocolate_products": "Som v chládku! :( Čokoláda má kvôli vysokým teplotám dovolenku.",
    "voucher_permalink": "mixit-plus-na-12-mesiacov"
},
    "pl": {
    "composition": "Skład",
    "from": "Od",
    "mixit_plus_price": "cena",
    "own_piece_of_italy": "Spróbuj prawdziwie włoskiego smaku",
    "play_video": "Zobacz wideo",
    "selected_chocolate_products": "Czekam w chłodzie! :( Ze względu na upały, czekolada została wysłana na przymusowy urlop.",
    "voucher_permalink": "mixit-plus-na-12-miesiecy"
},
    "hu": {
    "composition": "Összetevők",
    "from": "Ft-tól",
    "mixit_plus_price": "ár",
    "own_piece_of_italy": "Egy falat Olaszország",
    "play_video": "Nézd meg a videót",
    "selected_chocolate_products": "Hidegre helyezve! :( A magas hőmérsékletek miatt a csokoládés termékek szabadságon vannak.",
    "voucher_permalink": "mixit-plusz-12-honapra"
},
    "de": {
    "composition": "Zutaten",
    "from": "Von",
    "mixit_plus_price": "preis",
    "own_piece_of_italy": "Erlebe dein eigenes Stück Italien",
    "play_video": "Video ansehen",
    "selected_chocolate_products": "Ich liege auf Eis! :( Wegen der hohen Temperaturen haben sie mich in die Kühlung geschickt.",
    "voucher_permalink": "mixit-plus-fuer-12-monate"
},
    "at": {
    "composition": "Zutaten",
    "from": "Von",
    "mixit_plus_price": "preis",
    "own_piece_of_italy": "Erlebe dein eigenes Stück Italien",
    "play_video": "Video ansehen",
    "selected_chocolate_products": "Ich liege auf Eis! :( Wegen der hohen Temperaturen haben sie mich in die Kühlung geschickt.",
    "voucher_permalink": "mixit-plus-fuer-12-monate"
},
    "ro": {
    "composition": "Ingrediente",
    "from": "De la",
    "mixit_plus_price": "preț",
    "own_piece_of_italy": "Descoperă o bucațică de Italia",
    "play_video": "Urmărește clipul",
    "selected_chocolate_products": "La rece! :( Din cauza temperaturilor înalte produsele cu ciocolată sunt în concediu.",
    "voucher_permalink": "mixit-plus-de-12-luni"
},
    "fr": {
    "composition": "Composition",
    "from": "À partir de",
    "mixit_plus_price": "prix",
    "own_piece_of_italy": "Offrez-vous un bout d’Italie",
    "play_video": "Regardez la vidéo",
    "selected_chocolate_products": "Je suis au frais ! À cause des températures élevées, ils m'ont plongée dans le froid.",
    "voucher_permalink": "mixit-plus-pour-12 mois"
}
  }
</i18n>
